/**
 * When each employment change type gets written, spelled out step by step.
 *
 * Written for BOTH audiences. HR needs to know what to click and what will happen,
 * a developer needs to know the exact condition that triggers the row. So every
 * entry is the same three parts:
 *
 *    when    the exact condition. What has to be true for this to happen
 *    then    what the system does as a result
 *    result  what actually appears on the timeline afterwards
 *
 * Keyed by employment_change_type.org_change_type_id. Those ids are fixed and
 * seeded, so they mean the same thing on test and on production. NEVER key this on
 * the name, because names can be renamed from the Change Types screen.
 *
 * Ids 1 to 13 are built in. Anything above 13 is a custom type HR added, and
 * nothing writes those automatically.
 */
export const CHANGE_TYPE_HELP = {
  1: {
    when: 'The person has no employment record at all, and you save the Employee Tagging screen. Or you click Create history on their history screen.',
    then: 'An employment record is created, starting from their joining date. A first period is filled in with the department, wing, designation, role, reporting head and office already on their record.',
    result: 'This line appears dated their joining date, and is marked as seeded. It is marked because we only know they hold that role now, not that they held it the whole time.',
  },

  2: {
    when: 'Their employment status was Resigned or Terminated, and you change it to Working or Long Leave.',
    then: 'A confirmation appears first. If you confirm, the Date of Joining, Probation Completed On, Exit Date and Exit Reason are cleared. You must enter a NEW joining date, and it has to be after the previous exit date.',
    result: 'A SECOND employment record is created and this line appears on the new joining date. The old record is kept untouched, and the gap between the old exit date and the new joining date is the time they were away.',
  },

  3: {
    when: 'You set the employment status to Resigned or Terminated AND enter an exit date. Both are needed, the status on its own does nothing.',
    then: 'Save asks you to confirm, naming the date and reason. On confirm the employment record is closed on that date and the current period is closed with it.',
    result: 'This line appears on the exit date. The person stops counting as a current employee. It cannot be undone from the screen, though the exit date and reason can still be corrected afterwards.',
  },

  4: {
    when: 'You save a role that belongs to a different DEPARTMENT from the one before.',
    then: 'The period they were in is closed today, and a new one is opened holding the new position.',
    result: 'This line appears showing the old and new department. It almost always appears together with a wing, designation and role change, because all four are decided by the single role you pick.',
  },

  5: {
    when: 'You save a role that belongs to a different WING from the one before.',
    then: 'The period they were in is closed today, and a new one is opened holding the new position.',
    result: 'This line appears showing the old and new wing. Moving somebody to another department almost always changes the wing too, so the two usually appear together.',
  },

  6: {
    when: 'You save a role whose DESIGNATION is different from the one before.',
    then: 'The period they were in is closed today, and a new one is opened holding the new position.',
    result: 'This line appears showing the old and new designation. Note the same job title can exist in several wings, which is why the wing is shown beside each designation on the tagging screen.',
  },

  7: {
    when: 'You save a different ROLE, even when the designation and wing stay exactly the same. Several roles can sit under one designation.',
    then: 'The period they were in is closed today, and a new one is opened holding the new role.',
    result: 'This line appears showing the old and new role. This is the value actually stored against the person, the department, wing and designation are only used to find it.',
  },

  8: {
    when: 'You change the Reporting Head on the tagging screen and save.',
    then: 'The period they were in is closed today, and a new one is opened with the new reporting head.',
    result: 'This line appears showing who they reported to before and who they report to now.',
  },

  9: {
    when: 'You change the Office on the tagging screen and save.',
    then: 'The period they were in is closed today, and a new one is opened with the new office.',
    result: 'This line appears showing the old and new office.',
  },

  10: {
    when: 'You change the Employment Type and save, for example Probation to Permanent.',
    then: 'The period they were in is closed today, and a new one is opened with the new type. Choosing Permanent also makes the Probation Completed On field appear on the form.',
    result: 'This line appears showing the old and new employment type.',
  },

  11: {
    when: 'You change the Employment Status and save, for example Working to Long Leave.',
    then: 'The period they were in is closed today, and a new one is opened with the new status.',
    result: 'This line appears showing the old and new status. Choosing Resigned or Terminated instead is treated as an exit, and records an Exit line rather than this one.',
  },

  12: {
    when: 'You set the Probation Completed On date and save. That field only appears once Employment Type is Permanent, because it records the date probation FINISHED.',
    then: 'The date is stored on the employment record.',
    result: 'This line appears showing the date probation was completed.',
  },

  13: {
    when: 'You change the exit date or exit reason of somebody who has ALREADY left.',
    then: 'The existing employment record is updated in place. No new record is created and nothing is reopened.',
    result: 'This line appears so the correction is visible. This is NOT an undo. The person still counts as having left, you are only fixing the details of when or why.',
  },
};

/** Shown for any custom type, which is anything HR added themselves. */
export const CUSTOM_TYPE_HELP = {
  when: 'Never automatically. The system does not watch any field for this type.',
  then: 'Nothing happens on its own. It only exists so somebody can add it to a person’s timeline by hand.',
  result: 'This line appears only if a person deliberately adds it.',
};

/**
 * The explanation as one block of text, ready for a tooltip.
 * Render it with whiteSpace: pre-line so the line breaks survive.
 */
export const helpForChangeType = (id, isSystem) => {
  const help = CHANGE_TYPE_HELP[id] || (isSystem ? null : CUSTOM_TYPE_HELP);
  if (!help) return '';

  return [
    `WHEN\n${help.when}`,
    `THEN\n${help.then}`,
    `YOU SEE\n${help.result}`,
  ].join('\n\n');
};

/**
 * What the DATE on a timeline line actually is, in plain words.
 *
 * The date column follows the EMPLOYEE'S life, not the person recording it. For a
 * joining line it is the day they joined, for an exit line the day they left. For
 * an ordinary field change it is the day the change took effect, which is the day
 * it was saved. Hovering the date says which of those you are looking at, so
 * nobody has to work it out from the change type.
 *
 * Keyed by employment_change_type.org_change_type_id, same as the help above.
 */
const DATE_MEANING = {
  1: 'The day this person joined',
  2: 'The day this person came back',
  3: 'The day this person left',
  12: 'The day their probation was completed',
  13: 'The day the correction was made',
};

/** The date meaning for one line, with a sensible fallback for a field change. */
export const meaningOfDate = (id) =>
  DATE_MEANING[id] || 'The day this change took effect';

/**
 * The thing that confuses people most, so it is said once on any screen that
 * lists these.
 */
export const MULTIPLE_CHANGES_NOTE =
  'One save can write several of these lines at once. Moving somebody to a role in another department writes a department change, a wing change, a designation change and a role change together, because all four are decided by the single role you pick.';

/** Makes a tooltip keep the line breaks above, and stay readable. */
export const HELP_TOOLTIP_PROPS = {
  tooltip: {
    sx: {
      whiteSpace: 'pre-line',
      maxWidth: 360,
      fontSize: 12,
      lineHeight: 1.5,
      p: 1.5,
    },
  },
};
